// Offshore sector classifier (WFD-7; owner decision 11 of the 27 September 2026 review, design
// section 6.2 and 7.1 row WP-S0). One shared function for every offshore-drift warning:
//
// - red: the wind blows from within 22.5 degrees of dead offshore (the offshore octant);
// - amber: from 22.5 to 67.5 degrees (the two side-offshore octants);
// - clear: anything more onshore, or wind below the usable-wind gate;
// - unavailable: the wind is usable (or its speed is unknown) but the wind speed, the wind
//   direction or the beach facing is missing, so drift cannot be judged. A missing input is
//   never read as clear.
//
// Boundaries resolve to the more severe side: exactly 22.5 degrees is red, exactly 67.5 is
// amber, and a speed exactly at the gate is usable.
//
// Directions follow the meteorological convention the forecast uses: `windDirectionDeg` is where
// the wind blows FROM, and `beachFacingDeg` points from the beach out to sea, so dead offshore
// is a wind from `beachFacingDeg + 180`.
//
// Pure and dependency-free: it runs in the route, the page briefing (a client component) and
// the engines' per-hour v2 safety modules alike.

/** Red within this many degrees of dead offshore. */
export const OFFSHORE_RED_SECTOR_DEG = 22.5;
/** Amber from the red edge up to this many degrees of dead offshore. */
export const OFFSHORE_AMBER_SECTOR_DEG = 67.5;
/**
 * Default usable-wind gate in km/h: the lowest gate any offshore flag used before WFD-7 (wing
 * foil, 12 km/h; kite and windsurf use 14). A caller may pass its sport's own gate; the sectors
 * never change.
 */
export const OFFSHORE_USABLE_WIND_KMH = 12;
/**
 * Kite and windsurf keep the gate their offshore flag used before WFD-7 (14 km/h in
 * `assessWindSportsSafety`); wing foil keeps the default.
 */
export const WIND_SPORTS_OFFSHORE_USABLE_WIND_KMH = 14;

export type OffshoreSector = "offshore" | "side-offshore" | "other";
export type OffshoreSectorLevel = "red" | "amber" | "clear" | "unavailable";
export type OffshoreSectorReason =
  | "assessed"
  | "below-usable-wind"
  | "missing-wind-speed"
  | "missing-beach-facing"
  | "missing-wind-direction";

export type OffshoreSectorInput = Readonly<{
  windSpeedKmh: number | null | undefined;
  windDirectionDeg: number | null | undefined;
  beachFacingDeg: number | null | undefined;
  /** Usable-wind gate in km/h; defaults to OFFSHORE_USABLE_WIND_KMH. */
  usableWindKmh?: number;
}>;

export type OffshoreSectorResult = Readonly<{
  level: OffshoreSectorLevel;
  /** The sector the wind blows from; null when the direction or facing is missing. */
  sector: OffshoreSector | null;
  /** Degrees between the wind direction and dead offshore (0 to 180); null when not computable. */
  angleFromOffshoreDeg: number | null;
  reason: OffshoreSectorReason;
}>;

function finiteNumber(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/** Smallest circular difference between two directions, 0 to 180 degrees. */
export function circularDifferenceDeg(a: number, b: number): number {
  const difference = (((a - b) % 360) + 360) % 360;
  return difference > 180 ? 360 - difference : difference;
}

/** The direction a dead-offshore wind blows from, 0 (inclusive) to 360 (exclusive). */
export function offshoreFromDeg(beachFacingDeg: number): number {
  return (((beachFacingDeg + 180) % 360) + 360) % 360;
}

/** Degrees between a wind direction and dead offshore, or null when either input is missing. */
export function angleFromOffshoreDeg(
  windDirectionDeg: number | null | undefined,
  beachFacingDeg: number | null | undefined,
): number | null {
  if (!finiteNumber(windDirectionDeg) || !finiteNumber(beachFacingDeg)) return null;
  return circularDifferenceDeg(windDirectionDeg, offshoreFromDeg(beachFacingDeg));
}

/** The sector for an angle from dead offshore (see the header for the boundaries). */
export function offshoreSectorForAngle(angleDeg: number): OffshoreSector {
  if (angleDeg <= OFFSHORE_RED_SECTOR_DEG) return "offshore";
  if (angleDeg <= OFFSHORE_AMBER_SECTOR_DEG) return "side-offshore";
  return "other";
}

const SECTOR_LEVEL: Readonly<Record<OffshoreSector, OffshoreSectorLevel>> = Object.freeze({
  offshore: "red",
  "side-offshore": "amber",
  other: "clear",
});

/** Classifies offshore-drift risk for one reading (see the header). */
export function classifyOffshoreSector(input: OffshoreSectorInput): OffshoreSectorResult {
  const gate = input.usableWindKmh ?? OFFSHORE_USABLE_WIND_KMH;
  if (!finiteNumber(gate) || gate < 0) {
    throw new RangeError(`usableWindKmh must be a finite, non-negative number of km/h, got ${String(input.usableWindKmh)}`);
  }
  const angle = angleFromOffshoreDeg(input.windDirectionDeg, input.beachFacingDeg);
  const sector = angle === null ? null : offshoreSectorForAngle(angle);
  const result = (level: OffshoreSectorLevel, reason: OffshoreSectorReason): OffshoreSectorResult =>
    Object.freeze({ level, sector, angleFromOffshoreDeg: angle, reason });

  const speed = input.windSpeedKmh;
  if (!finiteNumber(speed) || speed < 0) return result("unavailable", "missing-wind-speed");
  if (speed < gate) return result("clear", "below-usable-wind");
  if (!finiteNumber(input.beachFacingDeg)) return result("unavailable", "missing-beach-facing");
  if (!finiteNumber(input.windDirectionDeg)) return result("unavailable", "missing-wind-direction");
  // angle and sector are non-null here: both directions are finite.
  return result(SECTOR_LEVEL[sector as OffshoreSector], "assessed");
}
